import { Controller } from "@hotwired/stimulus"

// Live availability check for a text input against a JSON endpoint that
// returns `{ valid, available }`. Toggles Bootstrap's is-valid/is-invalid on
// the input, shows a feedback message, and disables the submit target while
// the value is invalid or taken.
//
// Markup:
//   <div data-controller="availability-check"
//        data-availability-check-url-value="/check"
//        data-availability-check-taken-message-value="That name is taken.">
//     <input data-availability-check-target="input">
//     <div data-availability-check-target="feedback"></div>
//     <button data-availability-check-target="submit">…</button>
//   </div>
//
// The endpoint receives the value as `?slug=<value>`.
export default class extends Controller {
  static targets = ["input", "feedback", "submit"]
  static values = {
    url: String,
    param: { type: String, default: "slug" },
    debounce: { type: Number, default: 300 },
    availableMessage: { type: String, default: "Available" },
    invalidMessage: { type: String, default: "Invalid value." },
    takenMessage: { type: String, default: "That value is taken." },
  }

  connect() {
    if (!this.hasInputTarget) return
    this.timer = null
    this.boundSchedule = () => this.schedule()
    this.inputTarget.addEventListener("input", this.boundSchedule)
    this.run()
  }

  disconnect() {
    clearTimeout(this.timer)
    if (this.hasInputTarget && this.boundSchedule) {
      this.inputTarget.removeEventListener("input", this.boundSchedule)
    }
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.run(), this.debounceValue)
  }

  run() {
    const value = this.inputTarget.value.trim()
    if (!value) {
      this.reset()
      return
    }
    const url = `${this.urlValue}?${this.paramValue}=${encodeURIComponent(value)}`
    fetch(url)
      .then((r) => r.json())
      .then((d) => this.render(d))
      .catch(() => {})
  }

  render({ valid, available }) {
    const ok = valid && available
    this.inputTarget.classList.toggle("is-valid", ok)
    this.inputTarget.classList.toggle("is-invalid", !ok)
    if (this.hasFeedbackTarget) {
      if (ok) {
        this.feedbackTarget.textContent = this.availableMessageValue
        this.feedbackTarget.className = "valid-feedback d-block"
      } else {
        this.feedbackTarget.textContent = valid
          ? this.takenMessageValue
          : this.invalidMessageValue
        this.feedbackTarget.className = "invalid-feedback d-block"
      }
    }
    this.setSubmitDisabled(!ok)
  }

  reset() {
    this.inputTarget.classList.remove("is-valid", "is-invalid")
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.textContent = ""
      this.feedbackTarget.className = "form-text"
    }
    this.setSubmitDisabled(false)
  }

  setSubmitDisabled(disabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled
  }
}
